import { initContract } from '@ts-rest/core'
import { z } from 'zod'

const c = initContract()
const prefix = '/v2'

// Zod schemas for SKU code data structures
const IncentiveItemSchemaForSku = z
  .object({
    id: z.string(),
    name: z.string(),
    picture: z.string().nullable().optional(),
    categoryId: z.string().nullable().optional(),
    price: z.number().optional(),
    description: z.string().optional(),
    type: z.string().optional(),
    skuCode: z.string().nullable().optional(),
    isActive: z.boolean().optional(),
    customerId: z.string().nullable().optional(),
  })
  .passthrough()

const SkuCodeIncentiveItemSchema = z
  .object({
    id: z.string(),
    skuCodeId: z.string().optional(),
    incentiveItemId: z.string(),
    incentiveItem: IncentiveItemSchemaForSku,
  })
  .passthrough()

const SkuCodeSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
    incentiveItems: z.array(SkuCodeIncentiveItemSchema),
  })
  .passthrough()

const CreateSkuCodeInputSchema = z.object({
  name: z.string().min(1, 'SKU code name is required'),
  incentiveItemIds: z
    .array(z.string())
    .min(1, 'At least one incentive item is required'),
})

const UpdateSkuCodeInputSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'SKU code name is required'),
  incentiveItemIds: z
    .array(z.string())
    .min(1, 'At least one incentive item is required'),
})

// Export types for backward compatibility
export type CreateSkuCodeInput = z.infer<typeof CreateSkuCodeInputSchema>
export type UpdateSkuCodeInput = z.infer<typeof UpdateSkuCodeInputSchema>
export type SkuCode = z.infer<typeof SkuCodeSchema>

export const skuCode = c.router({
  getAll: {
    method: 'GET',
    path: `${prefix}/sku-codes`,
    responses: {
      200: z.array(SkuCodeSchema),
      401: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get all SKU codes',
  },
  getById: {
    method: 'GET',
    path: `${prefix}/sku-codes/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: SkuCodeSchema.nullable(),
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get SKU code by ID',
  },
  create: {
    method: 'POST',
    path: `${prefix}/sku-codes`,
    body: CreateSkuCodeInputSchema,
    responses: {
      201: SkuCodeSchema,
      400: z.object({ message: z.string() }),
      401: z.object({ message: z.string() }),
      409: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Create a new SKU code',
  },
  update: {
    method: 'PUT',
    path: `${prefix}/sku-codes/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: UpdateSkuCodeInputSchema,
    responses: {
      200: SkuCodeSchema,
      400: z.object({ message: z.string() }),
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      409: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Update a SKU code',
  },
  delete: {
    method: 'DELETE',
    path: `${prefix}/sku-codes/:id`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: z.object({ success: z.boolean(), id: z.string() }),
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Delete a SKU code',
  },
})
